import React, { useState, useEffect } from 'react';
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  BasicItem,
  Character,
  CharacterAdvantage,
  CharacterBasicItem,
  CharacterClass,
  CharacterSpell,
  CharacterSpellbook,
  Money,
  PseudoItem,
  RolledValue,
  SKILLS,
  Spell,
  SpellList,
} from "@dndModels";
import { 
  COST_UNIT_MAP, 
  TIME_UNIT_MAP, 
  TIME_UNIT_MAP_SINGULAR 
} from "@dndModels/Constants";

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

// import ButtonBox from "@components/input/buttons/ButtonBox";

import ViewSpell from "@dndComponents/display/ViewSpell";
import SpellDetails from "@dndComponents/display/SpellDetails";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import Roller from "@dndComponents/other/Roller";

interface Props {
  char: Character;
  char_class: CharacterClass;
  spell: Spell | CharacterSpell;
  // spell_list: (Spell | CharacterSpell)[];
  always_known: Spell[];
  bonus_added: string[];
  // change_time: number;
  processing: boolean;
  subgroup: string;
  extra_spells_mode: boolean;
  onChange: () => void;
  addSpell: (spell: Spell | CharacterSpell, extra?: boolean) => void;
  removeSpell: (spell: Spell | CharacterSpell) => void;
  char_spellbook?: CharacterSpellbook;
  char_spells: CharacterSpell[]; // These are the spells that are known or prepared for this group
  extra_spells: CharacterSpell[]; // These are the extra spells that are known or prepared for this group
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterManageSpellsRow({
  char,
  char_class,
  spell,
  // spell_list,
  always_known,
  bonus_added,
  // change_time,
  processing,
  subgroup,
  extra_spells_mode,
  onChange,
  addSpell,
  removeSpell,
  char_spellbook,
  char_spells,
  extra_spells
}: Props) {
  const [fetched, setFetched] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // const [prev_change_time, setChangeTime] = useState(change_time);
  const [prev_processing, setProcessing] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [spell_lists, setSpellLists] = useState<SpellList[]>([]);
  
  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setSpellLists(data_util.spell_lists);
      setFetched(true);
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (processing !== prev_processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing, setProcessing]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        '&.prepared': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.secondary.dark,
        },
        '&.unprepared': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.dark,
        },
      },
    });
  });
  
  const useRowStyles = makeStyles()((theme) => {
    const colors = char_util.getColors({ char: char, user: api.user, for_id: char_class.source_id });
    // console.log(colors);
    return ({
      root: {
        backgroundColor: colors.backgroundColor !== "" ? colors.backgroundColor : theme.palette.secondary.main,
        color: colors.color !== "" && colors.color !== "Default" ? colors.color : undefined
      },
    });
  });

  const renderBonus = () => {
    const bonus = char_class.bonus_spells.find(o => o.ff && o.ff.spell_ids.includes(spell._id));
    if (bonus && bonus.ff) {
      if (bonus_added.includes(spell._id)) {
        return (
          <Grid
            className="centered small_label gray"
            size={{
              xs: 12
            }}>Bonus Spell from { bonus.ff.name }
          </Grid>
        );
      } else {
        return (
          <>
            <Grid
              className="centered small_label gray"
              size={{
                xs: 12
              }}>
              Bonus Spell from { bonus.ff.name },
            </Grid>
            <Grid
              className="centered small_label gray"
              size={{
                xs: 12
              }}>
              but was already on Spell List
            </Grid>
          </>
        );
      }
    }
    return null;
  }

  const renderButton = () => {
    // if (spell.name.includes("Alarm")) {
    //   debugger;
    // }
    if (char_spellbook) {
      const free_spell_mod = char_spellbook.modifiers.find(o => 
        o.unused_free_spells > 0 && (o.schools.length === 0 || o.schools.includes(spell.school)));
        
      // if (spell.name.includes("Animate Obj")) {
      //   // debugger;
      //   console.log(char_spellbook.total_unused_free_spells);
      //   console.log(free_spell_mod);
      // }
      if ((spell.level > 0 && char_spellbook.spells.filter(o => o.spell_id === spell.spell_id).length === 0)) {
        const mods = char_spellbook.modifiers.filter(o => o.schools.length === 0 || o.schools.includes(spell.school));
        let time = char_spellbook.time_num_per_spell_level;
        let time_unit = char_spellbook.time_unit;
        let time_unit_str = "";
        let cost = char_spellbook.cost_per_spell_level;
        let cost_unit = char_spellbook.cost_unit;
        for (const mod of mods) {
          if (mod.time_unit < time_unit) {
            time = mod.time_num_per_spell_level;
            time_unit = mod.time_unit;
          } else if (mod.time_unit === time_unit && mod.time_num_per_spell_level < time) {
            time = mod.time_num_per_spell_level;
          }
          if (mod.cost_unit < cost_unit) {
            cost = mod.cost_per_spell_level;
            cost_unit = mod.cost_unit;
          } else if (mod.cost_unit === cost_unit && mod.cost_per_spell_level < cost) {
            cost = mod.cost_per_spell_level;
          }
        }

        cost *= spell.level;
        time *= spell.level;
        if (time === 1) {
          time_unit_str = TIME_UNIT_MAP_SINGULAR[time_unit];
        } else {
          time_unit_str = TIME_UNIT_MAP[time_unit];
        }

        if (free_spell_mod || char_spellbook.total_unused_free_spells > 0) {
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox
                  name={ "Add" }
                  disabled={processing}
                  onClick={() => {
                    addSpell(spell, true);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox
                  name={ `${cost} ${COST_UNIT_MAP[cost_unit]}` }
                  disabled={processing}
                  onClick={() => {
                    const cost2 = new Money();
                    switch (cost_unit) {
                      case 1: cost2.copper = cost; break;
                      case 2: cost2.silver = cost; break;
                      case 3: cost2.electrum = cost; break;
                      case 4: cost2.gold = cost; break;
                      case 5: cost2.platinum = cost; break;
                    }
                    char.money.subtract(cost2);
                    addSpell(spell, true);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox
                  name={ "Add Free" }
                  disabled={processing}
                  onClick={() => {
                    // debugger;
                    addSpell(spell);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 8
                }}>
                <span className='small_label gray'>{ `Takes ${time} ${time_unit_str}` }</span>
              </Grid>
              { renderBonus() }
            </Grid>
          );
        } else {
          const cost2 = new Money();
          switch (cost_unit) {
            case 1: cost2.copper = cost; break;
            case 2: cost2.silver = cost; break;
            case 3: cost2.electrum = cost; break;
            case 4: cost2.gold = cost; break;
            case 5: cost2.platinum = cost; break;
          }
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 6
                }}>
                <DandDButtonBox
                  name={ "Add" }
                  disabled={processing}
                  onClick={() => {
                    addSpell(spell, true);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <DandDButtonBox
                  name={ `Add for ${cost} ${COST_UNIT_MAP[cost_unit]}` }
                  disabled={ processing || char.money.gold_value() < cost2.gold_value() }
                  onClick={() => {
                    char.money.subtract(cost2);
                    addSpell(spell, true);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 12
                }}>
                <span className='small_label gray'>{ `Takes ${time} ${time_unit_str}` }</span>
              </Grid>
              { renderBonus() }
            </Grid>
          );
        }
      } else if (known_variant) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Variant Known"
                // style={{
                //   fontSize: "10px",
                //   lineHeight: "1.3"
                // }}
                disabled
                onClick={() => {
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      } else if (basically_always_known || always_known.filter(o => o._id === spell.spell_id).length > 0) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Always Known"
                disabled
                onClick={() => {
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Remove"
                className={cx(classes.root, "prepared")}
                disabled={processing}
                onClick={() => {
                  // debugger;
                  removeSpell(spell);
                  setPing(!ping);
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      }
    } else {
      if (char_spells.filter(o => (spell instanceof Spell && o.spell_id === spell.spell_id) || (spell instanceof CharacterSpell && o.true_id === spell.true_id)).length > 0) {
        if (known_variant) {
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name="Remove"
                  disabled={processing}
                  className={cx(classes.root, "prepared")}
                  onClick={() => {
                    removeSpell(spell);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                className="centered small_label gray"
                size={{
                  xs: 12
                }}>
                Variant Known
              </Grid>
              { renderBonus() }
            </Grid>
          );
        } else if (basically_always_known || always_known.filter(o => o._id === spell.spell_id).length > 0) {
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name="Remove"
                  disabled={processing}
                  className={cx(classes.root, "prepared")}
                  onClick={() => {
                    removeSpell(spell);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid
                className="centered small_label gray"
                size={{
                  xs: 12
                }}>
                Always Known
              </Grid>
              { renderBonus() }
            </Grid>
          );
        } else {
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name="Remove"
                  disabled={processing}
                  className={cx(classes.root, "prepared")}
                  onClick={() => {
                    removeSpell(spell);
                    setPing(!ping);
                  }}
                />
              </Grid>
              { renderBonus() }
            </Grid>
          );
        }
      } else if (extra_spells.filter(o => (spell instanceof Spell && o.spell_id === spell.spell_id) || (spell instanceof CharacterSpell && o.true_id === spell.true_id)).length > 0) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Remove Extra"
                disabled={processing}
                className={cx(classes.root, "prepared")}
                onClick={() => {
                  removeSpell(spell);
                  setPing(!ping);
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      } else if (known_variant) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Variant Known"
                disabled
                onClick={() => {
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      } else if (basically_always_known || always_known.filter(o => o._id === spell.spell_id).length > 0) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Always Known"
                disabled
                onClick={() => {
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name={ extra_spells_mode ? "Add Extra" : (char_class.knowledge_type === "Known" ? "Learn" : "Prepare") }
                disabled={ processing || 
                  (!extra_spells_mode && 
                    ((spell.level === 0 && 
                      (char_class.cantrips_max <= char_class.cantrips.length)) || 
                    (spell.level > 0 && 
                      (char_class.spells_prepared_max <= char_class.leveled_spells.length)))) 
                }
                onClick={() => {
                  addSpell(spell);
                  setPing(!ping);
                }}
              />
            </Grid>
            { renderBonus() }
          </Grid>
        );
      }
    }
  }
  
  const renderExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { popoverAnchorEl !== null && renderRoll() }
        </Popover>
      </>
    );
  }
  
  const renderRoll = () => {
    // Rolls an Arcana check, and tells you if you succeeded
    const skill = SKILLS.find(o => o.name === "Arcana");
    const dc = 10 + spell.level;

    if (skill) {
      // const { skill_rolls: rolls } = char_util.skillRolls(character, skill);
      // const { advantage } = char_util.getAdvantage(character, "Ability Check", skill);
      // const minimum_roll = char_util.getMinimumRoll(character, "Ability Check", skill);
      return (
        <Roller 
          name={skill.name}
          // char={character}
          // char_interface={character}
          // advantage={advantage}
          // minimum_roll={minimum_roll}
          rolled_values={roll} 
          type="Skill Check" 
          dc={dc}
        />
      );
    }
  }

  const fitsExtraBaseLimits = (extra_base_limits: { levels: string[], schools: string[], onSpellList: string[] }) => {
    const level_string = spell.level === 0 ? "Cantrip" : `${spell.level}`;
    if (extra_base_limits.levels.length > 0 && !extra_base_limits.levels.includes("Any") &&
      !extra_base_limits.levels.includes(level_string)) {
      return false;
    } else {
      const spell_list_ids = spell_lists.filter(o => spell.spell_list_ids.includes(o._id)).map(o => o._id);
      if (extra_base_limits.onSpellList.length > 0 && !extra_base_limits.onSpellList.includes("Any") &&
        !common_util.anyOverlaps(extra_base_limits.onSpellList, spell_list_ids)) {
        return false;
      } else if (extra_base_limits.schools.length > 0 && !extra_base_limits.schools.includes("Any") &&
        !extra_base_limits.schools.includes(spell.school)) {
        return false;
      }
    }
    return true;
  }
  
  const addScroll = async () => {
    // debugger;
    // extra_base_limits: { levels: string[], schools: string[], onSpellList: string[] }
    const item = data_util.items.find(o => 
      o.item_type === "Scroll" && 
      o instanceof BasicItem && 
      o.extra_base_type === "Spell" && 
      fitsExtraBaseLimits(o.extra_base_limits));
    if (item instanceof BasicItem) {
      const pseudo_item = new PseudoItem();
      pseudo_item.base_item = item;
      pseudo_item.extra = spell.spell_id;
      pseudo_item.extra_spell = spell instanceof CharacterSpell ? spell.spell : spell;
      await connect_util.addItem({ char, item: pseudo_item });
    }
    onChange();
  }

  // if (spell.name.includes("Locate Creature")) {
  //   debugger;
  // }
  const { known_from_other_source, known_variant, basically_always_known } = char_util.getSpellStuff({ char: char, char_class, char_spellbook, spell });

  const { classes, cx } = useStyles();
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  if (char_spellbook) {
    let expanded_content: any = null;
    if (expanded) {
      const mods = char_spellbook.modifiers.filter(o => o.schools.length === 0 || o.schools.includes(spell.school));
      let time = char_spellbook.time_num_per_spell_level;
      let time_unit = char_spellbook.time_unit;
      let time_unit_str = "";
      let cost = char_spellbook.cost_per_spell_level;
      let cost_unit = char_spellbook.cost_unit;
      for (const mod of mods) {
        if (mod.time_unit < time_unit) {
          time = mod.time_num_per_spell_level;
          time_unit = mod.time_unit;
        } else if (mod.time_unit === time_unit && mod.time_num_per_spell_level < time) {
          time = mod.time_num_per_spell_level;
        }
        if (mod.cost_unit < cost_unit) {
          cost = mod.cost_per_spell_level;
          cost_unit = mod.cost_unit;
        } else if (mod.cost_unit === cost_unit && mod.cost_per_spell_level < cost) {
          cost = mod.cost_per_spell_level;
        }
      }

      cost *= spell.level;
      time *= spell.level;
      if (time === 1) {
        time_unit_str = TIME_UNIT_MAP_SINGULAR[time_unit];
      } else {
        time_unit_str = TIME_UNIT_MAP[time_unit];
      }

      const skill = SKILLS.find(o => o.name === "Arcana");
      const dc = 10 + spell.level;
      let arcana_string = "+0";
      let use_advantage = 0;
      let use_advantages: CharacterAdvantage[] = [];
      let use_gr_adv = false;

      if (skill) {
        const { bonuses_string } = char_util.skillRolls(char, skill);
        const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char, "Ability Check", skill);

        arcana_string = bonuses_string;
        use_advantage = advantage;
        use_advantages = advantages;
        use_gr_adv = greater_advantage;
      }

      const cost2 = new Money();
      switch (cost_unit) {
        case 1: cost2.copper = cost; break;
        case 2: cost2.silver = cost; break;
        case 3: cost2.electrum = cost; break;
        case 4: cost2.gold = cost; break;
        case 5: cost2.platinum = cost; break;
      }

      const char_scroll = char.inventory.items.find(o => o.item_type === "Scroll" && o.extra_spell && o.extra_spell._id === spell.spell_id && o.count > 0);

      const char_spell = char_spells.find(o => (spell instanceof Spell && o.spell_id === spell.spell_id) || (spell instanceof CharacterSpell && o.true_id === spell.true_id));
      const known = char_spell !== undefined || (known_variant || known_from_other_source || basically_always_known);
      expanded_content = (
        <>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 12
              }}>
              <SpellDetails spell={spell} />
            </Grid>
            <Grid
              size={{
                xs: char_spell && !char_spell.extra ? 6 : (known ? 12 : 4)
              }}>
              <DandDButtonBox
                name="Create Scroll"
                disabled={ processing }
                onClick={() => {
                  // debugger;
                  // Adds a scroll for this spell to inventory
                  if (char_scroll instanceof CharacterBasicItem) {
                    char_scroll.count++;
                    onChange();
                  } else {
                    setProcessing(true);
                    addScroll().then(() => {
                      setProcessing(false);
                    });
                  }
                }}
              />
            </Grid>
            { char_spell && !char_spell.extra ? 
              <Grid
                className='centered'
                size={{
                  xs: 6
                }}>
                Learned For Free
              </Grid>
            : !known &&
              <>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <DandDButtonBox
                    name={ `Add for ${cost} ${COST_UNIT_MAP[cost_unit]} and Scroll` }
                    disabled={ processing || char.money.gold_value() < cost2.gold_value() || char_scroll === undefined }
                    onClick={() => {
                      // Removes cost and scroll, and learns spell
                      const item = char_scroll;
                      if (item instanceof CharacterBasicItem) {
                        item.count--;
                        char.money.subtract(cost2);
                        if (item.count === 0) {
                          connect_util.removeItem(char, item);
                        }
                      }
                      addSpell(spell);
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <DandDButtonBox
                    name={`Arcana Check: ${arcana_string}, DC ${dc}`}
                    advantage={ use_advantage }
                    advantages={ use_advantages }
                    greater_advantage={ use_gr_adv }
                    disabled={ processing }
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      const skill = SKILLS.find(o => o.name === "Arcana");
                      if (skill) {
                        const { skill_rolls: rolls } = char_util.skillRolls(char, skill);
                        // const { advantage } = char_util.getAdvantage(character, "Ability Check", skill);
                        const minimum_roll = char_util.getMinimumRoll(char, "Ability Check", skill);
                        // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Ability Check")) && skill.use_ability_score !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(skill.use_ability_score))).length > 0;
                        setRoll(char_util.rollD20({ char: char, rolls, type: "Ability Check", use_ability_score: skill.use_ability_score, advantage: use_advantage, greater_advantage: use_gr_adv, minimum_roll }));
                      }
                      setPopoverAnchorEl(event.currentTarget);
                    }}
                  />
                  { renderExtras() }
                </Grid>
                <Grid
                  className="centered"
                  size={{
                    xs: 12
                  }}>
                  <span className='small_label gray'>{ `Takes ${time} ${time_unit_str}` }</span>
                </Grid>
              </>
            }
          </Grid>
        </>
      );
    }
    return (
      <Grid container spacing={0} direction="row" className={cx2(rowClasses.root, "hola")}>
        <Grid
          container
          spacing={0}
          direction="column"
          className="padded"
          onClick={() => {
            setExpanded(!expanded);
          }}
          size={{
            xs: 6
          }}>
          <ViewSpell spell={spell} 
            show_level 
            show_ritual={char_class.ritual_casting} 
          />
        </Grid>
        <Grid
          style={{ margin: "auto" }}
          size={{
            xs: 6
          }}>
          { !reloading && renderButton() }
          { known_from_other_source && <span className='small_label gray centered'>Known from Another Source</span> }
        </Grid>
        { expanded_content }
      </Grid>
    );
  } else {
    let expanded_content: any = null;
    if (expanded) {
      expanded_content = (
        <Grid
          size={{
            xs: 12
          }}>
          <SpellDetails spell={spell} />
        </Grid>
      );
    }
    return (
      <Grid container spacing={0} direction="row" className={cx2(rowClasses.root)}>
        <Grid
          container
          spacing={0}
          direction="column"
          className="padded"
          onClick={() => {
            setExpanded(!expanded);
          }}
          size={{
            xs: 6
          }}>
          <ViewSpell spell={spell} 
            show_level 
            show_ritual={char_class.ritual_casting} 
          />
        </Grid>
        <Grid
          style={{ margin: "auto" }}
          size={{
            xs: 6
          }}>
          { !reloading && renderButton() }
          { known_from_other_source && <span className='small_label gray centered'>Known from Another Source</span> }
        </Grid>
        { expanded_content }
      </Grid>
    );
  }
}
